import { CommonModule } from '@angular/common';
import { Component, HostBinding, computed, effect, inject, input, output, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AddToCartActionComponent } from 'src/app/category/components/add-to-cart-action/add-to-cart-action.component';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { ProductListingVariant } from 'src/app/products/models/product-listing.model';
import { ProductDealCountdown } from 'src/app/products/models/product.model';
import { DealCountdownService } from 'src/app/products/services/deal-countdown.service';
import { InterpolationPipe } from 'src/app/shared/pipes/interpolation.pipe';
import { SafehtmlPipe } from 'src/app/shared/pipes/safehtml.pipe';

@Component({
  standalone: true,
  selector: 'webstore-dynamic-home-product-card',
  templateUrl: './dynamic-home-product-card.component.html',
  styleUrls: ['./dynamic-home-product-card.component.scss'],
  providers: [DealCountdownService],
  imports: [
    CommonModule,
    RouterModule,
    AddToCartActionComponent,
    InterpolationPipe,
    SafehtmlPipe
  ]
})
export class DynamicHomeProductCardComponent {
  private dealCountdownService = inject(DealCountdownService);
  private readonly deviceDetectionService = inject(DeviceDetectionService);



  readonly baseHtml = input<string>("");
  readonly showCountdown = input<boolean>(false);
  readonly showCashpoint = input<boolean>(false);
  readonly showPercentageDiscount = input<boolean>(true);
  readonly showAddToCart = input<boolean>(false);
  readonly productInput = input<ProductListingVariant>({} as ProductListingVariant);

  // Product signal with internal state
  private _product = signal<ProductListingVariant | null>(null);
  get product(): ProductListingVariant {
    return this._product() || {} as ProductListingVariant;
  }

  // Update outputs to use Angular 20 output() function
  addToCartClicked$ = output<boolean>();
  productClicked$ = output<void>();

  // Badge styling signals
  badgeTextColor = signal('#ffffff');
  badgeBackgroundColor = signal('#ac0236');

  @HostBinding('style.--timer-color') get timerColor() {
    return this.badgeTextColor();
  }

  @HostBinding('style.--timer-bg-color') get timerBackgroundColor() {
    return this.badgeBackgroundColor();
  }

  // Product deal countdown signal
  productDealCountDown = signal<ProductDealCountdown | null>(null);

  // Deal availability signal
  isDealAvailable = signal(false);

  // Remaining time computed signal
  readonly remainingTime = computed(() => this.dealCountdownService.remainingTime());

  // Device detection computed signals (for template usage)
  readonly isMobile = this.deviceDetectionService.isMobile;
  readonly isDesktop = this.deviceDetectionService.isDesktop;
  readonly isTablet = this.deviceDetectionService.isTablet;
  readonly deviceType = this.deviceDetectionService.deviceType;

  constructor() {
    // Component is now using DeviceDetectionService for device detection

    effect(() => {
      const product = this.productInput();
      if (product) {
        this._product.set(product);
        this.isDealAvailable.set(!!product?.deal);
        this.productDealCountDown.set(product?.deal || null);
        this.badgeTextColor.set(product.deal?.text_color || '#ffffff');
        this.badgeBackgroundColor.set(product.deal?.background_color || '#ac0236');
      }
    });

    // Effect to handle countdown initialization when deal becomes available
    effect(() => {
      const dealAvailable = this.isDealAvailable();
      const productDeal = this.productDealCountDown();

      if (dealAvailable && productDeal?.end_time) {
        const endDay = new Date(productDeal.end_time);
        this.dealCountdownService.getCountDown(endDay);
      }
    });
  }

  onProductClick(): void {
    this.productClicked$.emit();
  }

  onAddToCartClicked(event: boolean) {
    this.addToCartClicked$.emit(event);
  }
}

